// /src/turnServices/SpeakService.js
import store from '@/store'
import { apiRequest } from '@/utils/apiRequest'

export class SpeakService {
	constructor() {
		this.audioContext = null
		this.audioUnlocked = false

		// Path suffixes only; full URL is resolved at play time from config store (api_base_url from systemConfig)
		this.kitchenOrderReadyAlertPath = '/world/snd/bellring.mp3'
		this.accountSeatedAlertPath = '/world/snd/doorbell-electro.mp3'
		this.weNotificationAlertPath = '/world/snd/notification-we.mp3'
		this.checkCopilotMessagePath = '/world/snd/we-connection-check.mp3'

		this.audioElements = new Map()   // url → primed <audio>
		this.currentAudio = null

		this.notificationQueue = []
		this.isPlaying = false

		this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
	}

	/** API base URL from system config (api_url), with fallback to env then https://api.wewelcom.io */
	getApiBase() {
		return store.getters['config/apiBaseUrl']
	}

	/** Full URL for a path under /world (sounds, notification audio). Uses config store so it follows backend systemConfig. */
	getSoundUrl(path) {
		const base = this.getApiBase().replace(/\/$/, '')
		return `${base}${path.startsWith('/') ? '' : '/'}${path}`
	}

	/* ---------- INITIALISATION / UNLOCK ----------------------------------- */

	async initializeAudioContext() {
		if (this.audioContext) return
		const AC = window.AudioContext || window.webkitAudioContext
		this.audioContext = new AC()
		this.audioContext.onstatechange = () => {
			if (this.audioContext.state === 'interrupted') {
				console.log('AudioContext interrupted – user gesture needed again')
			}
		}
	}

	async unlockAudio() {
		await this.initializeAudioContext()
		if (this.audioContext.state === 'suspended') {
			await this.audioContext.resume()
		}
		this.audioUnlocked = this.audioContext.state === 'running'
	}

	async unlockAudioIOS() {
		if (!this.isIOS) return

		/* 1. unlock context with silent buffer */
		const buffer = this.audioContext.createBuffer(1, 1, 22050)
		const src = this.audioContext.createBufferSource()
		src.buffer = buffer
		src.connect(this.audioContext.destination)
		src.start(0)

		/* 2. prime alert sounds so <audio> is gesture-activated */
		const prime = async (audio) => {
			audio.volume = 0
			await audio.play()
			audio.pause()
			audio.currentTime = 0
			audio.volume = 1
		}

		for (const path of [
			this.weNotificationAlertPath,
			this.kitchenOrderReadyAlertPath,
			this.accountSeatedAlertPath,
			this.checkCopilotMessagePath
		]) {
			const url = this.getSoundUrl(path)
			const el = await this.preloadAudio(url)
			await prime(el)
		}
	}

	/** Call once from the first real user gesture (tap / click) */
	async onUserInteraction() {
		await this.unlockAudio()
		await this.unlockAudioIOS()
	}

	/* ---------- HELPERS ---------------------------------------------------- */

	/** Preload sound but don’t play */
	async preloadAudio(url) {
		if (this.audioElements.has(url)) return this.audioElements.get(url)

		const audio = new Audio()
		audio.preload = 'auto'

		return new Promise((resolve, reject) => {
			audio.oncanplaythrough = () => {
				this.audioElements.set(url, audio)
				resolve(audio)
			}
			audio.onerror = reject
			audio.src = url
			audio.load()
		})
	}

	/** Unified player with automatic fallback */
	async playAudio(url) {
		if (!this.audioUnlocked) return

		/* quick path – reuse primed element */
		if (this.audioElements.has(url)) {
			const audio = this.audioElements.get(url)
			if (this.currentAudio && this.currentAudio !== audio) this.currentAudio.pause()
			this.currentAudio = audio
			audio.currentTime = 0
			await audio.play()
			return
		}

		/* try Web-Audio buffer first … */
		try {
			await this.playViaBuffer(url)                 // may throw (CORS/404)
			return
		} catch (err) {
			console.warn('[SpeakService] playViaBuffer failed, fallback:', err)
		}

		/* … and fallback to plain <audio> */
		const audio = new Audio(url)
		// Only cache non-blob URLs (alert sounds); notification TTS blobs are one-time, do not cache
		if (!url.startsWith('blob:')) {
			this.audioElements.set(url, audio)
		}

		this.currentAudio = audio
		await new Promise((res, rej) => {
			audio.onended = res
			audio.onerror = rej
			audio.play().catch(rej)
		})
	}

	/** Web-Audio branch (requires CORS) */
	async playViaBuffer(url) {
		const res = await fetch(url, { mode: 'cors' })
		if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`)

		const data = await res.arrayBuffer()
		const buffer = await this.audioContext.decodeAudioData(data)

		const src = this.audioContext.createBufferSource()
		src.buffer = buffer
		src.connect(this.audioContext.destination)
		src.start(0)

		return new Promise((resolve) => (src.onended = resolve))
	}

	/* ---------- QUEUE & PUBLIC API ---------------------------------------- */

	async enqueueNotification(notification, playAlertSound = true) {
		if (!this.audioUnlocked) return
		this.notificationQueue.push({ notification, playAlertSound })
		await this.processQueue()
	}

	async processQueue() {
		if (this.isPlaying) return
		const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

		while (this.notificationQueue.length) {
			const { notification, playAlertSound } = this.notificationQueue.shift()
			const delayMs = Number(store.getters['copilot/notificationMode']) * 60 * 1000
			try {
				this.isPlaying = true
				await this.playNotification(notification, playAlertSound)
				await sleep(delayMs)
			} catch (e) {
				console.error('Notification error:', e)
			} finally {
				this.isPlaying = false
			}
		}
	}

	async playNotificationAlertSound() {
		await this.playAudio(this.getSoundUrl(this.weNotificationAlertPath))
	}

	async playKitchenOrderReadyNotificationAlertSound() {
		await this.playAudio(this.getSoundUrl(this.kitchenOrderReadyAlertPath))
	}

	async playAccountSeatedNotificationAlertSound() {
		await this.playAudio(this.getSoundUrl(this.accountSeatedAlertPath))
	}

	async playCheckCopilotSound() {
		await this.playAudio(this.getSoundUrl(this.checkCopilotMessagePath))
	}

	/**
	 * Fetch notification TTS audio as a Blob (for use when we want to start loading in parallel with the alert).
	 * Backend requires JWT (ROLE_COPILOT for the venue).
	 */
	async fetchNotificationAudioBlob(notification) {
		const url = this.getSoundUrl(`/world/notification/audio/${notification.id}`)
		const { data } = await apiRequest({ url, method: 'GET', expect: 'blob' })
		return data
	}

	/**
	 * Play TTS audio for a notification. The backend requires JWT (ROLE_COPILOT for the venue).
	 * Use this when you already have the flow (e.g. replay from UI); for the main flow use playNotification
	 * which fetches in parallel with the alert to minimize delay.
	 */
	async playNotificationMessageAudio(notification) {
		const blob = await this.fetchNotificationAudioBlob(notification)
		const blobUrl = URL.createObjectURL(blob)
		try {
			await this.playAudio(blobUrl)
		} finally {
			URL.revokeObjectURL(blobUrl)
		}
	}

	/**
	 * Delay in ms between alert sound end and notification TTS start. Tune per type for natural timing
	 * (e.g. 0 if the alert has trailing silence, or a small value if the alert has a sharp cutoff).
	 */
	getNotificationGapMs(notification) {
		const gapByType = {
			order_ready_food: 0,
			account_seated: 0,
			table_take_order_reminder: 0,
			table_clean_reminder: 0,
			// default: 0
		}
		return gapByType[notification?.type] ?? 0
	}

	async playNotification(notification, playAlertSound = true) {
		const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

		// Start fetching notification audio immediately so it’s ready (or nearly ready) when the alert ends
		const blobPromise = this.fetchNotificationAudioBlob(notification)

		if (playAlertSound) {
			switch (notification.type) {
			case 'order_ready_food':
				await this.playKitchenOrderReadyNotificationAlertSound()
				break
			case 'account_seated':
			case 'table_take_order_reminder':
			case 'table_clean_reminder':
				await this.playAccountSeatedNotificationAlertSound()
				break
			default:
				await this.playNotificationAlertSound()
				break
			}
			const gapMs = this.getNotificationGapMs(notification) + 200;
			if (gapMs > 0) await sleep(gapMs)
		}

		const blob = await blobPromise
		const blobUrl = URL.createObjectURL(blob)
		try {
			await this.playAudio(blobUrl)
		} finally {
			URL.revokeObjectURL(blobUrl)
		}
	}

	async speak(message, playAlertSound = true) {
		if (playAlertSound) await this.playNotificationAlertSound()
		const utt = new SpeechSynthesisUtterance(message)
		utt.rate = 1
		utt.pitch = 1
		utt.lang = 'es-ES'
		setTimeout(() => window.speechSynthesis.speak(utt), 600)
	}
}

/* Singleton instance */
export const speakService = new SpeakService()
